import { CurrencyMapping } from 'api/account';
import { CostRate as CostRateType } from 'api/cost-rates';
import { ProjectCustomRates } from 'api/project';
import { RateCard } from 'api/rate-cards';

export enum AuditLogActionType {
  CostRate = 'COST_RATE_UPDATE',
  ProfectFinancials = 'PROJECT_FINANCIALS_FIELD_CHANGE',
  Currency = 'CURRENCY_UPDATE',
}

/**
 * This enum is used to identify resource type in the project audit log framework
 */
export enum FinancialsAuditLogMetaName {
  ProjectFinancialHours = 'PROJECT_HOURS',
  ProjectFinancialMinutes = 'PROJECT_MINUTES',
  ProjectFinancialBudget = 'PROJECT_BUDGET',
  ProjectBillRates = 'BILL_RATES',
}

/**
 * This enum is used to identify resource type in the general audit log framework
 */
export enum AuditLogMetaName {
  CostRate = 'COST_RATE',
  Currency = 'CURRENCY',
}

// Creating an internal type to identify meta specifically.
export type AuditLogMetaType = FinancialsAuditLogMetaName | AuditLogMetaName;

export enum AuditLogMetaChangeType {
  Added = 'ADDED',
  Updated = 'UPDATED',
  // Removed = 'REMOVED', // * Commenting as not needed as of now
}

interface AuditLogMetaBase<T extends AuditLogMetaType> {
  metaType: T;
  changeType: AuditLogMetaChangeType;
  changedByUserId: number;
}

interface ProjectFinancialsAuditLogBase {
  notes?: string;
  newValue: number;
  oldNumber?: number;
}

export type ProjectBudgetAuditLogMeta =
  AuditLogMetaBase<FinancialsAuditLogMetaName.ProjectFinancialBudget> &
    ProjectFinancialsAuditLogBase;

export type BudgetedHoursAuditLogMeta = AuditLogMetaBase<
  | FinancialsAuditLogMetaName.ProjectFinancialHours
  | FinancialsAuditLogMetaName.ProjectFinancialMinutes
> &
  ProjectFinancialsAuditLogBase;

interface BillRatesAuditLogMeta
  extends AuditLogMetaBase<FinancialsAuditLogMetaName.ProjectBillRates> {
  newBillRatesBackDatedValue?: boolean;
  oldBillRatesBackDatedValue?: boolean;
  newRateCard?: Pick<
    RateCard,
    'rateCardId' | 'rateCardName' | 'rateCardVersions'
  >;
  oldRateCard?: Pick<
    RateCard,
    'rateCardId' | 'rateCardName' | 'rateCardVersions'
  >;
  newProjectCustomRates?: ProjectCustomRates[];
  oldProjectCustomRates?: ProjectCustomRates[];
  currentProjectCustomRates?: ProjectCustomRates[];
}

export interface CostRateAuditLogMeta
  extends AuditLogMetaBase<AuditLogMetaName.CostRate> {
  backdated: boolean;
  newCostRate: CostRateType;
  oldCostRate?: CostRateType;
}

export interface CurrencyAuditLogMeta
  extends AuditLogMetaBase<AuditLogMetaName.Currency> {
  backdated: boolean;
  newAccountCurrencyMapping: CurrencyMapping;
  oldAccountCurrencyMapping?: CurrencyMapping;
}

export type ProjectFinancialsAuditLogMeta =
  | ProjectBudgetAuditLogMeta
  | BudgetedHoursAuditLogMeta
  | BillRatesAuditLogMeta;

export type AuditLogMeta =
  | ProjectFinancialsAuditLogMeta
  | CostRateAuditLogMeta
  | CurrencyAuditLogMeta;

export interface AuditLog<T extends AuditLogMeta> {
  accountId: number;
  actionBy: number;
  actionTimestamp: number;
  actionType: AuditLogActionType;
  meta: T;
  changedByFirstName: string;
  changedByLastName: string;
  metaName?: FinancialsAuditLogMetaName;
}
